import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    IoAdd, IoTrash, IoSave, IoCheckmarkCircle,
    IoPauseCircle, IoChevronDown, IoChevronForward,
    IoNotifications, IoMail, IoChatbubbles,
    IoLogoWhatsapp, IoSettings, IoSwapHorizontal,
    IoRefresh, IoSearch
} from 'react-icons/io5';
import * as Sentry from '@sentry/react';

/**
 * Channel type metadata
 */
const CHANNEL_TYPES = [
    { value: 'Email', label: 'Email', icon: <IoMail size={20} />, color: '#0066cc', description: 'Traditional email notifications via configured mailboxes' },
    { value: 'Teams', label: 'Microsoft Teams', icon: <IoChatbubbles size={20} />, color: '#6264A7', description: 'Channel posts and direct messages via Teams' },
    { value: 'WhatsApp', label: 'WhatsApp', icon: <IoLogoWhatsapp size={20} />, color: '#25D366', description: 'Mobile notifications via WhatsApp Business' }
];

/**
 * Trigger event options
 */
const TRIGGER_EVENTS = [
    { value: 'Created', label: 'Item Created', description: 'Fires when a new action item is created' },
    { value: 'Assigned', label: 'Item Assigned', description: 'Fires when an item is assigned or reassigned to a user' },
    { value: 'Commented', label: 'Comment Added', description: 'Fires when someone adds a comment to an item' },
    { value: 'StatusChanged', label: 'Status Changed', description: 'Fires when an item moves to a new status (e.g. Open → In Progress)' },
    { value: 'SlaBreached', label: 'SLA Breached', description: 'Fires when an item exceeds its SLA response or resolution time' },
    { value: 'Escalated', label: 'Escalated', description: 'Fires when an item is escalated to a higher priority or team' },
    { value: 'Completed', label: 'Completed', description: 'Fires when an item is marked as resolved or completed' }
];

const FUNCTIONAL_AREAS = [
    { value: 'General', label: 'General', description: 'Standard action items and ad-hoc tasks' },
    { value: 'ITServiceManagement', label: 'IT Service Management', description: 'IT support tickets, change requests, and onboarding' },
    { value: 'ActionItems', label: 'Action Items', description: 'Items from SIPA inspections, checklists, and compliance' },
    { value: 'AssetManagement', label: 'Asset Management', description: 'Asset service, repairs & maintenance tracking' },
    { value: 'Custom', label: 'Custom', description: 'Custom modules and integrations' }
];

/**
 * NotificationChannelsPage — manage notification delivery channels
 */
const NotificationChannelsPage = () => {
    const { apiFetch, user } = useAuth();
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editChannel, setEditChannel] = useState(null);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [secrets, setSecrets] = useState([]);

    // Discovery state
    const [discoveredTeams, setDiscoveredTeams] = useState([]);
    const [discoveredChannels, setDiscoveredChannels] = useState([]);
    const [discoveredMailboxes, setDiscoveredMailboxes] = useState([]);
    const [loadingTeams, setLoadingTeams] = useState(false);
    const [loadingChannels, setLoadingChannels] = useState(false);
    const [loadingMailboxes, setLoadingMailboxes] = useState(false);

    useEffect(() => {
        fetchChannels();
        fetchSecrets();
    }, []);

    const fetchChannels = async () => {
        try {
            const res = await apiFetch('/api/NotificationChannels');
            const data = await res.json();
            if (data.success) setChannels(data.data || []);
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Failed to load notification channels');
        } finally {
            setLoading(false);
        }
    };

    const fetchSecrets = async () => {
        try {
            const res = await apiFetch('/api/AppSecrets/by-category');
            const data = await res.json();
            if (data.success) {
                const all = [];
                Object.values(data.data).forEach(list => {
                    list.forEach(s => all.push(s));
                });
                setSecrets(all);
            }
        } catch (e) {
            Sentry.captureException(e);
        }
    };

    const handleCreate = (channelType) => {
        setIsCreateMode(true);
        setEditChannel({
            displayName: '',
            channelType,
            isEnabled: true,
            priority: 100,
            appSecretId: null,
            functionalAreas: '',
            actionItemTypes: '',
            triggerEvents: '',
            channelTarget: '',
            teamsTeamId: '',
            teamsChannelId: '',
            sendDirectMessage: false,
            isFallback: false,
            deepLinkTemplate: '{ORIGIN}/action-items/{ActionItemId}',
            description: ''
        });
    };

    const handleEdit = (channel) => {
        setIsCreateMode(false);
        setEditChannel({ ...channel });
    };

    const closeModal = () => {
        setEditChannel(null);
        setIsCreateMode(false);
        setSaving(false);
        setDiscoveredTeams([]);
        setDiscoveredChannels([]);
        setDiscoveredMailboxes([]);
    };

    const handleFieldChange = (field, value) => {
        setEditChannel(prev => ({ ...prev, [field]: value }));
    };

    const handleToggleEvent = (event) => {
        const current = (editChannel.triggerEvents || '').split(',').filter(Boolean);
        const updated = current.includes(event)
            ? current.filter(e => e !== event)
            : [...current, event];
        handleFieldChange('triggerEvents', updated.join(','));
    };

    const handleToggleFunctionalArea = (area) => {
        const current = (editChannel.functionalAreas || '').split(',').filter(Boolean);
        const updated = current.includes(area)
            ? current.filter(a => a !== area)
            : [...current, area];
        handleFieldChange('functionalAreas', updated.join(','));
    };

    const handleSave = async () => {
        if (!editChannel.displayName?.trim()) {
            toast.error('Display name is required');
            return;
        }
        setSaving(true);
        try {
            const method = isCreateMode ? 'POST' : 'PUT';
            const url = isCreateMode
                ? '/api/NotificationChannels'
                : `/api/NotificationChannels/${editChannel.id}`;

            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...editChannel, userId: user?.userID || user?.id })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                toast.success(data.message);
                closeModal();
                fetchChannels();
            } else {
                toast.error(data.message || 'Failed to save');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error saving channel');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (channel, e) => {
        e.stopPropagation();
        try {
            const res = await apiFetch(`/api/NotificationChannels/${channel.id}/toggle`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                fetchChannels();
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Failed to toggle channel');
        }
    };

    const handleDeleteClick = (channel, e) => {
        e.stopPropagation();
        setDeleteTarget(channel);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            const res = await apiFetch(`/api/NotificationChannels/${deleteTarget.id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                fetchChannels();
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error deleting channel');
        } finally {
            setDeleteTarget(null);
        }
    };

    const getChannelMeta = (type) => CHANNEL_TYPES.find(t => t.value === type) || CHANNEL_TYPES[0];
    const getRelevantSecrets = (channelType) => {
        if (channelType === 'Teams') return secrets.filter(s => s.secretType === 'Configuration' || s.secretType === 'AzureAd');
        if (channelType === 'WhatsApp') return secrets.filter(s => s.secretType === 'ApiKey');
        return secrets.filter(s => s.secretType === 'EmailConfig');
    };

    const discoverTeams = async (secretId) => {
        if (!secretId) return;
        setLoadingTeams(true);
        setDiscoveredTeams([]);
        setDiscoveredChannels([]);
        try {
            const res = await apiFetch(`/api/NotificationChannels/discover/teams?secretId=${secretId}`);
            const data = await res.json();
            if (data.success) {
                setDiscoveredTeams(data.data || []);
                if ((data.data || []).length === 0) toast.info('No Teams found for this credential');
            } else {
                toast.error(data.message || 'Failed to discover Teams');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error discovering Teams');
        } finally {
            setLoadingTeams(false);
        }
    };

    const discoverChannels = async (teamId, secretId) => {
        if (!teamId || !secretId) return;
        setLoadingChannels(true);
        setDiscoveredChannels([]);
        try {
            const res = await apiFetch(`/api/NotificationChannels/discover/teams/${teamId}/channels?secretId=${secretId}`);
            const data = await res.json();
            if (data.success) {
                setDiscoveredChannels(data.data || []);
                if ((data.data || []).length === 0) toast.info('No channels found in this Team');
            } else {
                toast.error(data.message || 'Failed to discover channels');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error discovering channels');
        } finally {
            setLoadingChannels(false);
        }
    };

    const discoverMailboxes = async (secretId) => {
        if (!secretId) return;
        setLoadingMailboxes(true);
        setDiscoveredMailboxes([]);
        try {
            const res = await apiFetch(`/api/NotificationChannels/discover/mailboxes?secretId=${secretId}`);
            const data = await res.json();
            if (data.success) {
                setDiscoveredMailboxes(data.data || []);
                if ((data.data || []).length === 0) toast.info('No mailboxes found for this credential');
            } else {
                toast.error(data.message || 'Failed to discover mailboxes');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error discovering mailboxes');
        } finally {
            setLoadingMailboxes(false);
        }
    };

    const handleSecretChange = (secretId) => {
        handleFieldChange('appSecretId', secretId ? parseInt(secretId) : null);
        // Reset discovery data when secret changes
        setDiscoveredTeams([]);
        setDiscoveredChannels([]);
        setDiscoveredMailboxes([]);
    };

    const handleTeamSelect = (teamId) => {
        handleFieldChange('teamsTeamId', teamId);
        handleFieldChange('teamsChannelId', '');
        setDiscoveredChannels([]);
        if (teamId && editChannel?.appSecretId) {
            discoverChannels(teamId, editChannel.appSecretId);
        }
    };

    const selectedEvents = (editChannel?.triggerEvents || '').split(',').filter(Boolean);
    const selectedAreas = (editChannel?.functionalAreas || '').split(',').filter(Boolean);

    return (
        <div className="nc-page">
            <div className="nc-header">
                <div>
                    <h2><IoNotifications size={24} /> Notification Channels</h2>
                    <p className="nc-subtitle">
                        Configure how action item notifications are delivered. Each channel can be scoped by module, event type, and priority.
                        Channels require credentials configured under <strong>App Secrets &amp; Integrations</strong> in the System settings category.
                    </p>
                </div>
            </div>

            {/* Channel type cards for creating */}
            <div className="nc-type-cards">
                {CHANNEL_TYPES.map(type => (
                    <div key={type.value} className="nc-type-card" onClick={() => handleCreate(type.value)}>
                        <div className="nc-type-icon" style={{ color: type.color }}>{type.icon}</div>
                        <div>
                            <h4>{type.label}</h4>
                            <p>{type.description}</p>
                        </div>
                        <IoAdd size={20} className="nc-type-add" />
                    </div>
                ))}
            </div>

            {/* Channel list */}
            {loading ? (
                <div className="nc-loading">Loading channels...</div>
            ) : channels.length === 0 ? (
                <div className="nc-empty">
                    <IoNotifications size={48} />
                    <h3>No notification channels configured</h3>
                    <p>Click one of the channel types above to add your first notification channel.</p>
                </div>
            ) : (
                <div className="nc-list">
                    {channels.map(ch => {
                        const meta = getChannelMeta(ch.channelType);
                        return (
                            <div
                                key={ch.id}
                                className={`nc-card ${ch.isEnabled ? '' : 'disabled'}`}
                                onClick={() => handleEdit(ch)}
                            >
                                <div className="nc-card-left">
                                    <div className="nc-card-icon" style={{ color: meta.color }}>{meta.icon}</div>
                                    <div className="nc-card-info">
                                        <h4>{ch.displayName}</h4>
                                        <span className="nc-card-type">{meta.label}</span>
                                        {ch.description && <p className="nc-card-desc">{ch.description}</p>}
                                        {ch.functionalAreas && (
                                            <div className="nc-card-tags">
                                                {ch.functionalAreas.split(',').map(a => (
                                                    <span key={a} className="nc-tag">{a.trim()}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="nc-card-right">
                                    <span className="nc-priority">Priority: {ch.priority}</span>
                                    {ch.isFallback && <span className="nc-badge fallback">Fallback</span>}
                                    {ch.sendDirectMessage && <span className="nc-badge dm">DM</span>}
                                    <div className="nc-card-actions">
                                        <button
                                            className={`nc-btn-icon ${ch.isEnabled ? '' : 'off'}`}
                                            onClick={(e) => handleToggleActive(ch, e)}
                                            title={ch.isEnabled ? 'Disable' : 'Enable'}
                                        >
                                            {ch.isEnabled ? <IoCheckmarkCircle size={18} /> : <IoPauseCircle size={18} />}
                                        </button>
                                        <button
                                            className="nc-btn-icon delete"
                                            onClick={(e) => handleDeleteClick(ch, e)}
                                            title="Delete"
                                        >
                                            <IoTrash size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Edit/Create Modal */}
            {editChannel && (
                <div className="nc-modal-overlay" onClick={closeModal}>
                    <div className="nc-modal" onClick={e => e.stopPropagation()}>
                        <div className="nc-modal-header">
                            <h3>{isCreateMode ? 'Add Notification Channel' : 'Edit Channel'}</h3>
                            <button className="nc-close" onClick={closeModal}>&times;</button>
                        </div>
                        <div className="nc-modal-body">
                            {/* Channel Type Banner */}
                            {(() => {
                                const meta = getChannelMeta(editChannel.channelType);
                                return (
                                    <div className="nc-type-banner" style={{ borderLeftColor: meta.color }}>
                                        <span style={{ color: meta.color }}>{meta.icon}</span>
                                        <span>{meta.label} Channel</span>
                                    </div>
                                );
                            })()}

                            {/* Basic Info */}
                            <div className="nc-form-section">
                                <h4>Basic Information</h4>
                                <div className="nc-form-row">
                                    <div className="nc-field">
                                        <label>Display Name *</label>
                                        <input
                                            type="text"
                                            value={editChannel.displayName || ''}
                                            onChange={e => handleFieldChange('displayName', e.target.value)}
                                            placeholder="e.g., IT Support Teams Channel"
                                        />
                                        <small>A friendly name to identify this channel in the list (e.g., &quot;IT Support Alerts&quot; or &quot;Finance Email Notifications&quot;)</small>
                                    </div>
                                    <div className="nc-field nc-field-narrow">
                                        <label>Priority</label>
                                        <input
                                            type="number"
                                            value={editChannel.priority}
                                            onChange={e => handleFieldChange('priority', parseInt(e.target.value) || 100)}
                                            min={1}
                                        />
                                        <small>Lower number = higher priority. When multiple channels match, lower-priority channels send first.</small>
                                    </div>
                                </div>
                                <div className="nc-field">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        value={editChannel.description || ''}
                                        onChange={e => handleFieldChange('description', e.target.value)}
                                        placeholder="What this channel is used for"
                                    />
                                    <small>Optional internal note for administrators (e.g., &quot;Sends critical alerts to the ops team&quot;)</small>
                                </div>
                            </div>

                            {/* Credentials */}
                            <div className="nc-form-section">
                                <h4>Credentials</h4>
                                <div className="nc-field">
                                    <label>Linked App Secret</label>
                                    <select
                                        value={editChannel.appSecretId || ''}
                                        onChange={e => handleSecretChange(e.target.value)}
                                    >
                                        <option value="">Select credentials...</option>
                                        {getRelevantSecrets(editChannel.channelType).map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.displayName || s.secretKey} ({s.secretType})
                                            </option>
                                        ))}
                                    </select>
                                    <small>
                                        {editChannel.channelType === 'Teams' && 'Link to an MS Teams or Azure AD secret configured under App Secrets'}
                                        {editChannel.channelType === 'WhatsApp' && 'Link to a WhatsApp Business secret configured under App Secrets'}
                                        {editChannel.channelType === 'Email' && 'Link to an Email Config secret (mailbox credentials)'}
                                    </small>
                                </div>
                            </div>

                            {/* Channel-specific configuration */}
                            {editChannel.channelType === 'Teams' && (
                                <div className="nc-form-section">
                                    <h4>Teams Configuration</h4>
                                    <div className="nc-field">
                                        <label>Webhook URL or Channel Target</label>
                                        <input
                                            type="url"
                                            value={editChannel.channelTarget || ''}
                                            onChange={e => handleFieldChange('channelTarget', e.target.value)}
                                            placeholder="https://yourtenant.webhook.office.com/webhookb2/..."
                                        />
                                        <small>Incoming webhook URL for the Teams channel. Required for webhook mode. Leave blank to use Graph API mode below.</small>
                                    </div>

                                    <div className="nc-discover-section">
                                        <div className="nc-discover-header">
                                            <span className="nc-discover-label">Graph API Mode</span>
                                            <small>Auto-discover Teams and channels from the linked credential</small>
                                        </div>

                                        {/* Team Selection */}
                                        <div className="nc-field">
                                            <label>Team</label>
                                            <div className="nc-discover-row">
                                                {discoveredTeams.length > 0 ? (
                                                    <select
                                                        value={editChannel.teamsTeamId || ''}
                                                        onChange={e => handleTeamSelect(e.target.value)}
                                                        className="nc-discover-select"
                                                    >
                                                        <option value="">Select a Team...</option>
                                                        {discoveredTeams.map(t => (
                                                            <option key={t.id} value={t.id}>
                                                                {t.displayName}{t.description ? ` — ${t.description}` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={editChannel.teamsTeamId || ''}
                                                        onChange={e => handleFieldChange('teamsTeamId', e.target.value)}
                                                        placeholder={editChannel.appSecretId ? 'Click Discover to load teams' : 'Select a credential first'}
                                                        className="nc-discover-select"
                                                        readOnly={!!editChannel.appSecretId}
                                                    />
                                                )}
                                                <button
                                                    type="button"
                                                    className="nc-btn nc-btn-discover"
                                                    onClick={() => discoverTeams(editChannel.appSecretId)}
                                                    disabled={!editChannel.appSecretId || loadingTeams}
                                                    title="Discover Teams from Azure AD"
                                                >
                                                    {loadingTeams ? <span className="nc-spinner" /> : <IoSearch size={16} />}
                                                    {loadingTeams ? 'Loading...' : 'Discover'}
                                                </button>
                                            </div>
                                            {editChannel.teamsTeamId && <small className="nc-id-display">ID: {editChannel.teamsTeamId}</small>}
                                        </div>

                                        {/* Channel Selection */}
                                        <div className="nc-field">
                                            <label>Channel</label>
                                            <div className="nc-discover-row">
                                                {discoveredChannels.length > 0 ? (
                                                    <select
                                                        value={editChannel.teamsChannelId || ''}
                                                        onChange={e => handleFieldChange('teamsChannelId', e.target.value)}
                                                        className="nc-discover-select"
                                                    >
                                                        <option value="">Select a Channel...</option>
                                                        {discoveredChannels.map(c => (
                                                            <option key={c.id} value={c.id}>
                                                                {c.displayName}{c.membershipType ? ` (${c.membershipType})` : ''}{c.description ? ` — ${c.description}` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={editChannel.teamsChannelId || ''}
                                                        onChange={e => handleFieldChange('teamsChannelId', e.target.value)}
                                                        placeholder={editChannel.teamsTeamId ? 'Channels load automatically' : 'Select a team first'}
                                                        className="nc-discover-select"
                                                        readOnly={!!editChannel.teamsTeamId}
                                                    />
                                                )}
                                                {loadingChannels && <span className="nc-spinner nc-inline-spinner" />}
                                            </div>
                                            {editChannel.teamsChannelId && <small className="nc-id-display">ID: {editChannel.teamsChannelId}</small>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {editChannel.channelType === 'Email' && editChannel.appSecretId && (
                                <div className="nc-form-section">
                                    <h4>Email Configuration</h4>
                                    <div className="nc-field">
                                        <label>Mailbox / Send-As Address</label>
                                        <div className="nc-discover-row">
                                            {discoveredMailboxes.length > 0 ? (
                                                <select
                                                    value={editChannel.channelTarget || ''}
                                                    onChange={e => handleFieldChange('channelTarget', e.target.value)}
                                                    className="nc-discover-select"
                                                >
                                                    <option value="">Use default from secret</option>
                                                    {discoveredMailboxes.map(m => (
                                                        <option key={m.id} value={m.mail}>
                                                            {m.displayName} ({m.mail})
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={editChannel.channelTarget || ''}
                                                    onChange={e => handleFieldChange('channelTarget', e.target.value)}
                                                    placeholder="Leave empty to use default mailbox"
                                                    className="nc-discover-select"
                                                />
                                            )}
                                            <button
                                                type="button"
                                                className="nc-btn nc-btn-discover"
                                                onClick={() => discoverMailboxes(editChannel.appSecretId)}
                                                disabled={!editChannel.appSecretId || loadingMailboxes}
                                                title="Discover mailboxes from Azure AD"
                                            >
                                                {loadingMailboxes ? <span className="nc-spinner" /> : <IoSearch size={16} />}
                                                {loadingMailboxes ? 'Loading...' : 'Discover'}
                                            </button>
                                        </div>
                                        <small>Override the send-from address. Leave blank to use the default from the linked secret.</small>
                                    </div>
                                </div>
                            )}

                            {editChannel.channelType === 'WhatsApp' && (
                                <div className="nc-form-section">
                                    <h4>WhatsApp Configuration</h4>
                                    <div className="nc-field">
                                        <label>Message Template Override</label>
                                        <input
                                            type="text"
                                            value={editChannel.channelTarget || ''}
                                            onChange={e => handleFieldChange('channelTarget', e.target.value)}
                                            placeholder="captrac_action_item_update"
                                        />
                                        <small>Pre-approved WhatsApp template name. Leave empty to use the default from the WhatsApp secret</small>
                                    </div>
                                </div>
                            )}

                            {/* Deep Link */}
                            <div className="nc-form-section">
                                <h4>Action Item Link</h4>
                                <div className="nc-field">
                                    <label>Deep Link Template</label>
                                    <input
                                        type="text"
                                        value={editChannel.deepLinkTemplate || ''}
                                        onChange={e => handleFieldChange('deepLinkTemplate', e.target.value)}
                                        placeholder="{ORIGIN}/action-items/{ActionItemId}"
                                    />
                                    <small>URL template included in notification messages. Use {'{ActionItemId}'} placeholder. {'{ORIGIN}'} = your CapTrac base URL</small>
                                </div>
                            </div>

                            {/* Routing — Trigger Events */}
                            <div className="nc-form-section">
                                <h4>Trigger Events</h4>
                                <small className="nc-section-help">Choose which action item events send notifications through this channel. Leave all unchecked to notify on every event. Hover over an event for details.</small>
                                <div className="nc-chip-grid">
                                    {TRIGGER_EVENTS.map(evt => (
                                        <button
                                            key={evt.value}
                                            className={`nc-chip ${selectedEvents.includes(evt.value) ? 'active' : ''}`}
                                            onClick={() => handleToggleEvent(evt.value)}
                                            type="button"
                                            title={evt.description}
                                        >
                                            {evt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Routing — Functional Areas */}
                            <div className="nc-form-section">
                                <h4>Functional Area Scope</h4>
                                <small className="nc-section-help">Limit this channel to specific business modules. Leave all unchecked to apply to all areas. Hover over an area for details.</small>
                                <div className="nc-chip-grid">
                                    {FUNCTIONAL_AREAS.map(area => (
                                        <button
                                            key={area.value}
                                            className={`nc-chip ${selectedAreas.includes(area.value) ? 'active' : ''}`}
                                            onClick={() => handleToggleFunctionalArea(area.value)}
                                            type="button"
                                            title={area.description}
                                        >
                                            {area.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Options */}
                            <div className="nc-form-section">
                                <h4>Options</h4>
                                <small className="nc-section-help">Control how this channel behaves. Disabled channels are saved but won&apos;t send notifications.</small>
                                <div className="nc-checkboxes">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={editChannel.isEnabled}
                                            onChange={e => handleFieldChange('isEnabled', e.target.checked)}
                                        />
                                        Enabled
                                    </label>
                                    {editChannel.channelType !== 'Email' && (
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={editChannel.sendDirectMessage}
                                                onChange={e => handleFieldChange('sendDirectMessage', e.target.checked)}
                                            />
                                            Send Direct Messages to assigned user
                                        </label>
                                    )}
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={editChannel.isFallback}
                                            onChange={e => handleFieldChange('isFallback', e.target.checked)}
                                        />
                                        Fallback channel (only used when primary channel fails)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="nc-modal-footer">
                            <button className="nc-btn nc-btn-cancel" onClick={closeModal}>Cancel</button>
                            <button
                                className="nc-btn nc-btn-save"
                                onClick={handleSave}
                                disabled={saving || !editChannel.displayName?.trim()}
                            >
                                <IoSave size={16} />
                                {saving ? 'Saving...' : 'Save Channel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {deleteTarget && (
                <div className="nc-modal-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="nc-modal nc-modal-small" onClick={e => e.stopPropagation()}>
                        <div className="nc-modal-header">
                            <h3>Delete Channel</h3>
                        </div>
                        <div className="nc-modal-body">
                            <p>Delete <strong>{deleteTarget.displayName}</strong>? Notifications will no longer be sent through this channel.</p>
                        </div>
                        <div className="nc-modal-footer">
                            <button className="nc-btn nc-btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="nc-btn nc-btn-delete" onClick={handleDeleteConfirm}>
                                <IoTrash size={16} /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}



            <style>{`
                .nc-page { max-width: 960px; margin: 0 auto; padding: 20px; }
                .nc-header { margin-bottom: 20px; }
                .nc-header h2 { color: #57565B; display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
                .nc-subtitle { color: #888; font-size: 0.95rem; margin: 0; }
                .nc-loading, .nc-empty { text-align: center; padding: 40px; color: #888; }
                .nc-empty h3 { margin-top: 12px; }

                /* Type cards */
                .nc-type-cards { display: flex; gap: 12px; margin-bottom: 24px; flex-wrap: wrap; }
                .nc-type-card {
                    flex: 1; min-width: 220px; display: flex; align-items: center; gap: 12px;
                    background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; padding: 14px 16px;
                    cursor: pointer; transition: all 0.2s;
                }
                .nc-type-card:hover { border-color: #F63040; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
                .nc-type-card h4 { margin: 0; font-size: 0.95rem; color: #57565B; }
                .nc-type-card p { margin: 2px 0 0; font-size: 0.8rem; color: #999; }
                .nc-type-icon { flex-shrink: 0; }
                .nc-type-add { margin-left: auto; color: #ccc; flex-shrink: 0; }
                .nc-type-card:hover .nc-type-add { color: #F63040; }

                /* Channel list */
                .nc-list { display: flex; flex-direction: column; gap: 8px; }
                .nc-card {
                    display: flex; justify-content: space-between; align-items: center;
                    background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; padding: 14px 18px;
                    cursor: pointer; transition: all 0.15s;
                }
                .nc-card:hover { border-color: #F63040; }
                .nc-card.disabled { opacity: 0.55; }
                .nc-card-left { display: flex; align-items: center; gap: 14px; }
                .nc-card-icon { flex-shrink: 0; }
                .nc-card-info h4 { margin: 0; font-size: 0.95rem; color: #57565B; }
                .nc-card-type { font-size: 0.8rem; color: #999; }
                .nc-card-desc { font-size: 0.82rem; color: #aaa; margin: 2px 0 0; }
                .nc-card-tags { display: flex; gap: 4px; margin-top: 4px; flex-wrap: wrap; }
                .nc-tag { font-size: 0.72rem; background: #f0f0f0; padding: 2px 8px; border-radius: 10px; color: #666; }
                .nc-card-right { display: flex; align-items: center; gap: 10px; }
                .nc-priority { font-size: 0.78rem; color: #bbb; }
                .nc-badge { font-size: 0.72rem; padding: 2px 8px; border-radius: 10px; font-weight: 600; }
                .nc-badge.fallback { background: #fff3e0; color: #e67700; }
                .nc-badge.dm { background: #e8f5e9; color: #2e7d32; }
                .nc-card-actions { display: flex; gap: 4px; }
                .nc-btn-icon {
                    background: none; border: none; cursor: pointer; padding: 4px; border-radius: 4px; color: #4caf50;
                    transition: all 0.15s;
                }
                .nc-btn-icon.off { color: #999; }
                .nc-btn-icon.delete { color: #999; }
                .nc-btn-icon.delete:hover { color: #d32f2f; }

                /* Modal */
                .nc-modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .nc-modal {
                    background: #fff; border-radius: 10px; width: 90%; max-width: 680px; max-height: 85vh;
                    display: flex; flex-direction: column; box-shadow: 0 8px 32px rgba(0,0,0,0.15);
                }
                .nc-modal-small { max-width: 420px; }
                .nc-modal-header {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 16px 20px; border-bottom: 2px solid #F63040;
                }
                .nc-modal-header h3 { margin: 0; color: #57565B; }
                .nc-close { background: none; border: none; font-size: 1.4rem; cursor: pointer; color: #999; }
                .nc-modal-body { padding: 20px; overflow-y: auto; flex: 1; }
                .nc-modal-footer { padding: 14px 20px; border-top: 1px solid #eee; display: flex; justify-content: flex-end; gap: 10px; }

                /* Type banner */
                .nc-type-banner {
                    display: flex; align-items: center; gap: 8px; padding: 10px 14px;
                    background: #f8f9fa; border-left: 4px solid; border-radius: 6px; margin-bottom: 16px;
                    font-weight: 600; font-size: 0.95rem; color: #57565B;
                }

                /* Form */
                .nc-form-section { margin-bottom: 18px; }
                .nc-form-section h4 { color: #57565B; margin: 0 0 8px; font-size: 0.95rem; }
                .nc-section-help { display: block; color: #999; font-size: 0.82rem; margin-bottom: 8px; }
                .nc-form-row { display: flex; gap: 14px; flex-wrap: wrap; }
                .nc-field { flex: 1; min-width: 180px; margin-bottom: 10px; }
                .nc-field-narrow { max-width: 140px; }
                .nc-field label { display: block; font-weight: 600; color: #57565B; margin-bottom: 4px; font-size: 0.85rem; }
                .nc-field input, .nc-field select {
                    width: 100%; padding: 8px 10px; border: 1px solid #ddd; border-radius: 6px;
                    font-size: 0.9rem; box-sizing: border-box; transition: border-color 0.2s;
                }
                .nc-field input:focus, .nc-field select:focus { outline: none; border-color: #F63040; }
                .nc-field small { display: block; color: #bbb; font-size: 0.78rem; margin-top: 3px; }

                /* Chip grid */
                .nc-chip-grid { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
                .nc-chip {
                    padding: 5px 14px; border-radius: 16px; border: 1px solid #ddd; background: #fff;
                    font-size: 0.82rem; cursor: pointer; transition: all 0.15s; color: #666;
                }
                .nc-chip:hover { border-color: #F63040; }
                .nc-chip.active { background: #F63040; color: #fff; border-color: #F63040; }

                /* Checkboxes */
                .nc-checkboxes { display: flex; flex-direction: column; gap: 8px; }
                .nc-checkboxes label {
                    display: flex; align-items: center; gap: 8px; font-size: 0.9rem; color: #57565B; cursor: pointer;
                }

                /* Buttons */
                .nc-btn {
                    display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px;
                    border-radius: 6px; font-weight: 600; font-size: 0.9rem; cursor: pointer; border: none;
                    transition: all 0.2s;
                }
                .nc-btn:disabled { opacity: 0.6; cursor: not-allowed; }
                .nc-btn-save { background: #F63040; color: #fff; }
                .nc-btn-save:hover:not(:disabled) { background: #d42a38; }
                .nc-btn-cancel { background: #f5f5f5; color: #666; }
                .nc-btn-cancel:hover { background: #eee; }
                .nc-btn-delete { background: #d32f2f; color: #fff; }
                .nc-btn-delete:hover { background: #b71c1c; }

                /* Discovery */
                .nc-discover-section {
                    background: #f8f9fb; border: 1px solid #e4e7ec; border-radius: 8px;
                    padding: 14px; margin-top: 10px;
                }
                .nc-discover-header { margin-bottom: 12px; }
                .nc-discover-label {
                    font-weight: 700; font-size: 0.88rem; color: #57565B;
                    display: block; margin-bottom: 2px;
                }
                .nc-discover-header small { color: #999; font-size: 0.8rem; }
                .nc-discover-row { display: flex; gap: 8px; align-items: center; }
                .nc-discover-select { flex: 1; }
                .nc-btn-discover {
                    display: inline-flex; align-items: center; gap: 5px; padding: 8px 14px;
                    border-radius: 6px; font-weight: 600; font-size: 0.82rem; cursor: pointer;
                    border: 1px solid #6264A7; background: #fff; color: #6264A7;
                    transition: all 0.2s; white-space: nowrap; flex-shrink: 0;
                }
                .nc-btn-discover:hover:not(:disabled) { background: #6264A7; color: #fff; }
                .nc-btn-discover:disabled { opacity: 0.5; cursor: not-allowed; }
                .nc-id-display { color: #999; font-family: monospace; font-size: 0.75rem; }
                .nc-spinner {
                    width: 14px; height: 14px; border: 2px solid #ddd; border-top-color: #6264A7;
                    border-radius: 50%; animation: nc-spin 0.6s linear infinite; display: inline-block;
                }
                .nc-inline-spinner { margin-left: 8px; flex-shrink: 0; }
                @keyframes nc-spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default NotificationChannelsPage;
